import { AxiosRequestConfig } from 'axios';
import {
  AddOrderNotesAPI,
  POSTAddNotesOrderRequestType,
} from 'service/apis/CreateOrderapi';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { getOrderDetails } from '../apis';
import { OrderDetailResponse } from '../types/order';

export const useOrder = (orderId: string | undefined) => {
  const queryClient = useQueryClient();

  // Get the auth token
  const getAuthToken = () => {
    const auth = localStorage.getItem('authToken');
    if (!auth) return null;
    return JSON.parse(auth);
  };

  // Query for fetching order details
  const {
    data: orderDetails,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<OrderDetailResponse>({
    queryKey: ['orderDetails', orderId],
    queryFn: () => getOrderDetails(orderId as string),
    enabled: !!orderId && !!getAuthToken(),
  });

  // Mutation for adding notes
  const addNotesMutation = useMutation({
    mutationFn: async ({
      order_id,
      note,
      tcs_amount,
    }: {
      order_id: number;
      note: string;
      tcs_amount?: number;
    }) => {
      const authToken = getAuthToken();
      if (!authToken) throw new Error('Authentication token not found');

      const payload: AxiosRequestConfig<POSTAddNotesOrderRequestType> = {
        headers: { Authorization: `Bearer ${authToken}` },
        data: {
          order_id,
          note,
          ...(tcs_amount !== undefined && { tcs_amount }),
        },
      };

      return await AddOrderNotesAPI(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderDetails', orderId] });
    },
    onError: () => {
      toast.error('Failed to update details');
    },
  });

  // Function to add notes
  const addNotes = async (order_id: number, note: string) => {
    try {
      await addNotesMutation.mutateAsync({ order_id, note });
      return true;
    } catch (error) {
      return false;
    }
  };

  // Function to update TCS
  const updateTCS = async (order_id: number, note: string, tcs_amount: number) => {
    try {
      await addNotesMutation.mutateAsync({ order_id, note, tcs_amount });
      toast.success('TCS updated successfully');
      return true;
    } catch (error) {
      return false;
    }
  };

  // Utility function to download files
  const downloadFile = async (pdf_url: string, name: string) => {
    try {
      const response = await fetch(pdf_url);
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = name;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      return true;
    } catch (error) {
      return false;
    }
  };

  return {
    orderDetails,
    isLoading,
    isError,
    error,
    refetch,
    addNotes,
    updateTCS,
    downloadFile,
    isAddingNotes: addNotesMutation.isLoading,
  };
};